// src/features/rrhh/mensajeria/components/ChatThreadDrawer.jsx
import { Fragment, useLayoutEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIconLarge from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
//import { resolveBackendUrl } from "../../../../shared/utils/resolveBackendUrl";

function Bubble({ mine, text, ts, author, showAuthor = false }) {
  return (
    <Box
      sx={{
        alignSelf: mine ? "flex-end" : "flex-start",
        maxWidth: "78%",
        px: 1.5,
        py: 1,
        position: "relative",
        bgcolor: (t) =>
          mine ? (t.palette?.secondary?.[200] || "#AFBAD9") : (t.palette?.neutral?.[50] || "#F7F7F8"),
        boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",

        borderRadius: mine ? "18px 0px 18px 18px" : "0px 18px 18px 18px",

      }}
    >
      {showAuthor && author ? (
        <Typography
          sx={{
            fontSize: 10.5,
            fontWeight: 900,
            color: "primary.900",
            opacity: 0.72,
            mb: 0.35,
          }}
        >
          {author}
        </Typography>
      ) : null}

      <Typography sx={{ fontSize: 12, color: (t) => t.palette?.primary?.[900] }}>
        {text}
      </Typography>

      <Typography sx={{ fontSize: 10, opacity: 0.7, mt: 0.5, textAlign: "right" }}>
        {ts || ""}
      </Typography>
    </Box>
  );
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dayKeyFromIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function formatDayLabelES(iso) {
  const d = new Date(iso);
  const weekday = new Intl.DateTimeFormat("es-AR", { weekday: "long" }).format(d); // "jueves"
  const cap = weekday ? weekday[0].toUpperCase() + weekday.slice(1) : "";
  return `${cap} ${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}`;
}

function DayDivider({ label }) {
  return (
    <Box sx={{ display: "flex", justifyContent: "center", py: 0.75 }}>
      <Box
        sx={{
          px: 1.5,
          py: 0.5,
          borderRadius: 999,
          bgcolor: (t) => t.palette?.neutral?.[100],
          boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.25)",
        }}
      >
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: "primary.900", opacity: 0.85 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}


export default function ChatThreadDrawer({
  open,
  onClose,
  empleado,
  grupo,
  mode = "rrhh",
  thread = [],
  loading = false,
  sending = false,
  onSend,
}) {
  const [draft, setDraft] = useState("");
  const isGroupMode = mode === "group";
  const activeEntityId = isGroupMode ? grupo?.id_grupo : empleado?.id;

  const hasSelectedTarget = Boolean(activeEntityId);
  const emptyMessage = isGroupMode
    ? "Todavía no hay mensajes grupales."
    : "Todavía no hay mensajes en este chat.";

  const handleClose = () => {
    setDraft("");
    onClose?.();
  };

  const empleadoId = activeEntityId ?? null;
  const threadLen = thread?.length ?? 0;
  
  const listRef = useRef(null);

  const scrollToBottom = (behavior = "auto") => {
    const el = listRef.current;
    if (!el) return;

    // el es el div scrolleable (Box)
    el.scrollTo({ top: el.scrollHeight, behavior });
  };

  useLayoutEffect(() => {
    if (!open || !empleadoId) return;
    if (loading) return;

    // doble RAF + mini delay para asegurar layout + fuentes + animación
    requestAnimationFrame(() => {
      scrollToBottom("auto");
      requestAnimationFrame(() => scrollToBottom("auto"));
      setTimeout(() => scrollToBottom("auto"), 80);
    });
  }, [open, empleadoId, loading, threadLen]);


  const foto = null; // TODO

  const renderThreadWithDividers = () => {
    if (loading) {
      return <Typography sx={{ p: 1, opacity: 0.7 }}>Cargando mensajes…</Typography>;
    }

    // (Opcional pero recomendado) aseguramos orden por fecha ascendente
    const ordered = [...(thread || [])].sort((a, b) => {
      const ai = a?._raw?.fecha_envio ? new Date(a._raw.fecha_envio).getTime() : 0;
      const bi = b?._raw?.fecha_envio ? new Date(b._raw.fecha_envio).getTime() : 0;
      return ai - bi;
    });

    const now = new Date();
    const todayKey = `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;

    let prevKey = null;

    return ordered.map((m) => {
      // ✅ EN TU CASO el campo real es este:
      const iso = m?._raw?.fecha_envio || null;

      const k = dayKeyFromIso(iso);
      const showDivider = k && k !== prevKey;
      if (k) prevKey = k;

      const label = !k ? null : k === todayKey ? "Hoy" : formatDayLabelES(iso);

      return (
        <Fragment key={m.id}>
          {showDivider ? <DayDivider label={label} /> : null}
          <Bubble
            mine={m.from === "rrhh"}
            text={m.text}
            ts={m.ts}
            author={m.authorName}
            showAuthor={isGroupMode}
          />
        </Fragment>
      );
    });
  };

  return (
    <Drawer
      anchor="right"
      open={Boolean(open)}
      onClose={handleClose}
      ModalProps={{ keepMounted: true }}
      SlideProps={{
        onEntered: () => {
          requestAnimationFrame(() => scrollToBottom("auto"));
          requestAnimationFrame(() => scrollToBottom("auto"));
          setTimeout(() => scrollToBottom("auto"), 80);
        },
      }}
      PaperProps={{
        sx: {
          width: "32%",
          overflow: "hidden",
          bgcolor: (t) => t.palette?.neutral?.[200],
          boxShadow: "0 24px 80px rgba(0,0,0,.25)",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 2,
          pb: 0,
          bgcolor: (t) => t.palette?.neutral?.[200],
          position: "relative",
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 10,
            top: 10,
            width: 36,
            height: 36,
            bgcolor: "common.white",
            boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.25)",
            "&:hover": { bgcolor: (t) => t.palette?.neutral?.[50] },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Stack spacing={1.25} alignItems="center">
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: 999,
              bgcolor: (t) => t.palette?.primary?.[500],
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.35)",
            }}
          >
            {/* icon “chat” simplificado */}
            <Box
              sx={{
                width: 26,
                height: 18,
                borderRadius: 2,
                bgcolor: "common.white",
                position: "relative",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  left: 6,
                  bottom: -5,
                  width: 10,
                  height: 10,
                  bgcolor: "common.white",
                  transform: "rotate(45deg)",
                  borderBottomLeftRadius: 2,
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              px: 2,
              py: 0.75,
              borderRadius: 999,
              bgcolor: "common.white",
              boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.22)",
            }}
          >
            <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900" }}>
              {isGroupMode ? "Chat de grupo" : "Chat RRHH"}
            </Typography>
          </Box>

          {isGroupMode && grupo ? (
            <Box
              sx={{
                mt: 1,
                width: "100%",
                bgcolor: "common.white",
                borderRadius: 2,
                p: 1,
                gap: 1,
                alignItems: "center",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.22)",
                display: "flex",
                flexDirection: "row",
              }}
            >
              <Avatar
                sx={{
                  width: 60,
                  height: 60,
                  bgcolor: (t) => t.palette?.primary?.[900],
                  opacity: 0.95,
                  flex: "0 0 auto",
                }}
              >
                {(String(grupo?.area_nombre || "G").trim().slice(0, 1) || "G").toUpperCase()}
              </Avatar>

              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900", lineHeight: 1.4 }} noWrap>
                  {grupo.area_nombre || "Grupo"}
                </Typography>

                <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }}>
                  Sucursal: <b>{grupo.sucursal_nombre || "-"}</b>
                </Typography>

                <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }}>
                  Integrantes: <b>{grupo.miembros_count ?? 0}</b>
                </Typography>
              </Box>
            </Box>
          ) : empleado ? (
            <Box
              sx={{
                mt: 1,
                width: "100%",
                bgcolor: "common.white",
                borderRadius: 2,
                p: 1,
                gap: 1,
                alignItems: "center",
                boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.22)",
                display: "flex", 
                flexDirection: "row",
              }}
            >
              <Avatar
                src={foto || undefined}
                sx={{
                  width: 60,
                  height: 60,
                  bgcolor: (t) => t.palette?.primary?.[900],
                  opacity: 0.95,
                  flex: "0 0 auto",
                  boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.22)",
                }}
              >
                {!foto ? (String(empleado?.nombre || "?").trim().slice(0, 1) || "?").toUpperCase() : null}
              </Avatar>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900", lineHeight: 1.4 }} noWrap>
                  {empleado.nombre}
                </Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }}>
                  Estado: <b>Activo</b>
                </Typography>
                <Typography sx={{ fontSize: 12, opacity: 0.8, lineHeight: 1.4 }}>
                  Sucursal: <b>{empleado.sucursal}</b>
                </Typography>
              </Box>
            </Box>
          ) : null}
        </Stack>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", p: 2 }}>
        {!hasSelectedTarget ? (
          <Stack
            spacing={2}
            sx={{
              flex: 1,
              borderRadius: "20px",
              p: 3,
              bgcolor: (t) => t.palette?.neutral?.[50],
              boxShadow: "inset 0px 0px 3px rgba(165, 165, 178, 0.65)",
              justifyContent: "center",
              alignItems: "center",
              textAlign: "center",
            }}
          >
            <SearchIconLarge sx={{ fontSize: 74, color: (t) => t.palette?.primary?.[900] }} />
          
            <Typography sx={{ fontSize: 20, fontWeight: 900, color: "primary.900" }}>
              {isGroupMode ? "Seleccioná un grupo" : "Iniciá tu búsqueda"}
            </Typography>
          
            <Typography sx={{ fontSize: 14, opacity: 0.85, maxWidth: 420 }}>
              {isGroupMode
                ? "Elegí un grupo de área y sucursal para ver sus mensajes."
                : "Seleccioná un empleado en la lista o filtrá por área para gestionar los chats."}
            </Typography>
          </Stack>
        ) : (
          <>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                borderRadius: 2,
                bgcolor: "common.white",
                boxShadow: "inset 0px 0px 3px rgba(165, 165, 178, 0.65)",
                p: 1,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                ref={listRef}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  pr: 0.5,
                
                  scrollbarWidth: "none", // Firefox
                  "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
                  WebkitOverflowScrolling: "touch",
                  overscrollBehavior: "contain",
                }}
              >
                {loading ? (
                  <Stack
                    sx={{
                      flex: 1,
                      minHeight: 260,
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                    }}
                  >
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: "primary.900", opacity: 0.75 }}>
                      Cargando mensajes...
                    </Typography>
                  </Stack>
                ) : thread.length === 0 ? (
                  <Stack
                    spacing={1}
                    sx={{
                      minHeight: 260,
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      px: 3,
                    }}
                  >
                    <Typography sx={{ fontSize: 18, fontWeight: 900, color: "primary.900" }}>
                      {emptyMessage}
                    </Typography>
                  
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "primary.900", opacity: 0.72 }}>
                      {isGroupMode
                        ? "Cuando alguien escriba en este grupo, los mensajes van a aparecer acá."
                        : "Cuando haya mensajes con este empleado, van a aparecer acá."}
                    </Typography>
                  </Stack>
                ) : (
                  <Stack spacing={1} sx={{ py: 1 }}>
                    {renderThreadWithDividers()}
                  </Stack>
                )}
              </Box>
            </Box>


            <Box sx={{ mt: 1.25 }}>
              <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="Escribir mensaje..."
                size="small"
                sx={{
                  width: "100%",
                  "& .MuiOutlinedInput-root": {
                    height: 36,
                    borderRadius: 999,
                    bgcolor: (t) => t.palette?.neutral?.[50],
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.45)",
                    "& fieldset": { border: "none" },
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button
                        disabled={sending || !draft.trim()}
                        onClick={async () => {
                          const t = draft.trim();
                          if (!t) return;
                          await onSend?.(t);
                          setDraft("");
                        }}
                        sx={{
                          left: 14,
                          width: 20,
                          height: 36,
                          borderRadius: 999,
                          bgcolor: (t) => t.palette?.secondary?.[500],
                          color: "common.white",
                          "&:hover": { bgcolor: (t) => t.palette?.secondary?.[600] },
                        }}
                      >
                        <SendIcon sx={{ fontSize: 16 }} />
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
}
